import { useTellescopeInfiniteQuery } from "@tellescope/tanstack-query";
import { TellescopeFormFieldBaseProps } from "../../types";
import { MultiSelect, useDebouncedValue } from "@tellescope/ui";
import { transformDatabasePage } from "./transform-database-page";
import { useMemo, useState } from "react";
import { DatabaseSelectResponse } from "@tellescope/types-models";

export interface DatabaseSelectFieldProps extends TellescopeFormFieldBaseProps<"Database Select"> {}

export const DatabaseSelectField = ({
  id,
  title,
  value,
  onChange,
  className,
  options,
  onBlur,
  error,
}: DatabaseSelectFieldProps) => {
  const databaseId = options?.databaseId;
  const fieldId = id;
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery);
  const choicesQuery = useTellescopeInfiniteQuery({
    model: "form_fields",
    method: "load_choices_from_database",
    request: {
      databaseId,
      fieldId: fieldId ?? "",
      search: debouncedSearchQuery,
    },
    paginateRequest: (lastId, request) => ({ ...request, lastId }),
    initialPageParam: undefined as undefined | string,
    getNextPageParam: (lastPage) => lastPage.choices[0]?.id,
    enabled: !!databaseId && !!fieldId,
    select: (data) =>
      data.pages.flatMap((page) =>
        transformDatabasePage(page, { databaseLabel: options?.databaseLabel, databaseLabels: options?.databaseLabels })
      ),
  });

  const selectedRecordIds = useMemo(() => {
    return (
      value?.reduce<string[]>((acc, { recordId }) => {
        if (recordId) {
          acc.push(recordId);
        }
        return acc;
      }, []) ?? []
    );
  }, [value]);

  const handleOnValueChange = (recordIds: string[]) => {
    const records = recordIds.reduce<DatabaseSelectResponse[]>((acc, id) => {
      const match = choicesQuery.data?.find((item) => item.value === id);
      if (match) {
        acc.push({
          text: match.label,
          recordId: match.value,
          databaseId,
        });
      }
      return acc;
    }, []);
    onChange(records);
  };

  return (
    <div>
      {!!title && <h4>{title}</h4>}
      <MultiSelect
        value={selectedRecordIds}
        onValueChange={handleOnValueChange}
        onBlur={onBlur}
        options={choicesQuery.data ?? []}
        className={className}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        shouldFilter={false}
        isLoading={searchQuery !== debouncedSearchQuery || choicesQuery.isLoading}
      />
      {!!error && <p className="text-sm text-destructive mt-1">{error}</p>}
    </div>
  );
};
